import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, ClipboardList, CheckCircle2, FileText, XCircle, TrendingUp, Users, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusClass = (s) => s === 'Active' ? 'bg-emerald-600 text-white hover:bg-emerald-600'
  : s === 'Draft' ? 'bg-black text-white hover:bg-black'
  : s === 'Scheduled' ? 'bg-amber-500 text-white hover:bg-amber-500'
  : s === 'Closed' ? 'bg-rose-600 text-white hover:bg-rose-600'
  : 'bg-muted text-foreground';

export default function SurveysPage() {
  const { getAuthHeader, employee } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const canCreate = employee?.can_create_surveys || ['Super Admin', 'Admin Head'].includes(employee?.role);
  const isAdminHead = ['Super Admin', 'Admin Head'].includes(employee?.role);
  const isManagement = employee?.role === 'Management';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, lRes] = await Promise.all([
        fetch(`${API}/surveys/dashboard`, { headers: getAuthHeader() }),
        fetch(`${API}/surveys`, { headers: getAuthHeader() }),
      ]);
      if (dRes.ok) setDashboard(await dRes.json());
      if (lRes.ok) setRows(await lRes.json());
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const filtered = tab === 'all' ? rows : rows.filter(r => (r.effective_status || r.status) === (tab === 'draft' ? 'Draft' : tab === 'active' ? 'Active' : 'Closed'));

  const publish = async (id) => {
    const r = await fetch(`${API}/surveys/${id}/publish`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      if (d.scheduled && d.scheduled_at) {
        const when = new Date(d.scheduled_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
        toast.success(`Scheduled — goes live on ${when}. Invitation emails will be sent automatically then.`, { duration: 6000 });
      } else {
        toast.success(`Survey published${typeof d.invites_sent === 'number' ? ` · ${d.invites_sent} invite email(s) sent` : ''}`);
      }
      load();
    }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Publish failed'); }
  };
  const resendInvites = async (id) => {
    if (!window.confirm('Resend the invite email to everyone in this survey\'s audience?')) return;
    const r = await fetch(`${API}/surveys/${id}/resend-invites`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { const d = await r.json().catch(() => ({})); toast.success(`${d.invites_sent ?? 0} invite email(s) sent`); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Resend failed'); }
  };
  const close = async (id) => {
    if (!window.confirm('Close this survey?')) return;
    const r = await fetch(`${API}/surveys/${id}/close`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { toast.success('Survey closed'); load(); }
  };
  const duplicate = async (id) => {
    const r = await fetch(`${API}/surveys/${id}/duplicate`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { const d = await r.json(); toast.success(`Duplicated as ${d.survey_id}`); load(); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this survey? This permanently removes it and all its responses. This cannot be undone.')) return;
    const r = await fetch(`${API}/surveys/${id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('Survey deleted'); setSelected(prev => prev.filter(x => x !== id)); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Delete failed'); }
  };

  const deletableIds = filtered.filter(s => s.can_delete).map(s => s.survey_id);
  const allSelected = deletableIds.length > 0 && deletableIds.every(id => selected.includes(id));
  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(allSelected ? selected.filter(id => !deletableIds.includes(id)) : [...new Set([...selected, ...deletableIds])]);
  const bulkDelete = async () => {
    const ids = selected.filter(id => deletableIds.includes(id));
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} survey(s)? This permanently removes them and all their responses. This cannot be undone.`)) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        const r = await fetch(`${API}/surveys/${id}`, { method: 'DELETE', headers: getAuthHeader() });
        if (r.ok) ok++; else fail++;
      } catch { fail++; }
    }));
    setBulkDeleting(false);
    setSelected([]);
    if (fail > 0) toast.error(`${ok} deleted, ${fail} failed`);
    else toast.success(`${ok} survey(s) deleted`);
    load();
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="surveys-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <ClipboardList className="h-6 w-6" /> Surveys & Feedback
            </h1>
            <p className="text-muted-foreground">Design, publish and analyse enterprise surveys.</p>
          </div>
          <div className="flex gap-2">
            {isAdminHead && (
              <Button variant="outline" onClick={() => navigate('/surveys/permissions')} data-testid="manage-perms-btn">
                Manage Permissions
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => navigate('/surveys/new')} data-testid="new-survey-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Survey
              </Button>
            )}
          </div>
        </div>

        {/* KPI cards */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI icon={FileText} label="Total" value={dashboard.total} />
            <KPI icon={CheckCircle2} label="Active" value={dashboard.active} tone="emerald" />
            <KPI icon={ClipboardList} label="Draft" value={dashboard.draft} />
            <KPI icon={XCircle} label="Closed" value={dashboard.closed} tone="rose" />
            <KPI icon={TrendingUp} label="Responses" value={dashboard.total_responses} tone="indigo" />
          </div>
        )}

        {/* Upcoming expiry */}
        {dashboard?.upcoming_expiry?.length > 0 && (
          <Card className="border-amber-300 bg-amber-50/60 dark:bg-amber-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-900 dark:text-amber-300">
                <Clock className="h-4 w-4" /> Expiring within 7 days
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {dashboard.upcoming_expiry.map(s => (
                <div key={s.survey_id} className="flex justify-between">
                  <Link to={`/surveys/${s.survey_id}`} className="hover:underline">{s.title}</Link>
                  <span className="text-muted-foreground text-xs">{s.expires_at?.slice(0, 10)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All ({rows.length})</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
            <TabsTrigger value="draft" data-testid="tab-draft">Draft</TabsTrigger>
            <TabsTrigger value="closed" data-testid="tab-closed">Closed</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4">
            <Card>
              <CardContent className="pt-4">
                {filtered.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">No surveys yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    {selected.filter(id => deletableIds.includes(id)).length > 0 && (
                      <div className="flex items-center gap-3 mb-3 p-2 rounded-md bg-red-50 border border-red-200" data-testid="bulk-delete-bar">
                        <span className="text-sm font-medium text-red-800">{selected.filter(id => deletableIds.includes(id)).length} selected</span>
                        <Button size="sm" variant="destructive" onClick={bulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-btn">
                          <Trash2 className="h-4 w-4 mr-1" />{bulkDeleting ? 'Deleting…' : 'Delete Selected'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="bulk-clear-btn">Clear</Button>
                      </div>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {!isManagement && (
                            <TableHead className="w-8">
                              {deletableIds.length > 0 && (
                                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-surveys" aria-label="Select all" />
                              )}
                            </TableHead>
                          )}
                          <TableHead>ID</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Owner</TableHead>
                          <TableHead>Visibility</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Responses</TableHead>
                          <TableHead>Expires</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map(s => (
                          <TableRow key={s.survey_id} data-testid={`survey-row-${s.survey_id}`}>
                            {!isManagement && (
                              <TableCell className="w-8">
                                {s.can_delete && (
                                  <Checkbox
                                    checked={selected.includes(s.survey_id)}
                                    onCheckedChange={() => toggleSelect(s.survey_id)}
                                    data-testid={`select-survey-${s.survey_id}`}
                                    aria-label={`Select ${s.title}`}
                                  />
                                )}
                              </TableCell>
                            )}
                            <TableCell className="font-mono text-xs">{s.survey_id}</TableCell>
                            <TableCell className="font-medium">
                              <Link to={`/surveys/${s.survey_id}`} className="hover:underline">{s.title}</Link>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{s.created_by_name}</TableCell>
                            <TableCell><Badge variant="outline" className="text-[10px]">{s.visibility}</Badge></TableCell>
                            <TableCell><Badge className={statusClass(s.effective_status)}>{s.effective_status}</Badge></TableCell>
                            <TableCell className="text-right">{s.response_count || 0}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{s.expires_at?.slice(0, 10) || '—'}</TableCell>
                            <TableCell className="text-right space-x-1">
                              <Button size="sm" variant="ghost" onClick={() => navigate(`/surveys/${s.survey_id}`)} data-testid={`open-survey-${s.survey_id}`}>Open</Button>
                              {!isManagement && s.effective_status === 'Draft' && (
                                <Button size="sm" variant="outline" onClick={() => publish(s.survey_id)} data-testid={`publish-survey-${s.survey_id}`}>Publish</Button>
                              )}
                              {!isManagement && s.effective_status === 'Active' && (
                                <Button size="sm" variant="outline" onClick={() => close(s.survey_id)} data-testid={`close-survey-${s.survey_id}`}>Close</Button>
                              )}
                              {!isManagement && s.effective_status === 'Active' && (
                                <Button size="sm" variant="ghost" onClick={() => resendInvites(s.survey_id)} data-testid={`resend-survey-${s.survey_id}`}>Resend</Button>
                              )}
                              {!isManagement && canCreate && (
                                <Button size="sm" variant="ghost" onClick={() => duplicate(s.survey_id)} data-testid={`copy-survey-${s.survey_id}`}>Copy</Button>
                              )}
                              {!isManagement && s.can_delete && (
                                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(s.survey_id)} data-testid={`delete-survey-${s.survey_id}`}>Delete</Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function KPI({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    rose: 'text-rose-700 dark:text-rose-300',
    indigo: 'text-indigo-700 dark:text-indigo-300',
    slate: 'text-slate-700 dark:text-slate-300',
  };
  return (
    <Card>
      <CardContent className="pt-4 flex items-center gap-3">
        <Icon className={`h-8 w-8 ${tones[tone]}`} />
        <div>
          <div className="text-2xl font-bold">{value ?? 0}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
